import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAppSelector } from '../hooks/redux';
import { getActiveMessagingInstance } from '../services/messaging/providers/ServerMessagingProvider';
import { videoCallService, CallState, EndReason, CallerInfo } from '../services/VideoCallService';

interface VideoCallContextValue {
  callState: CallState;
  endReason: EndReason;
  incomingCaller: CallerInfo | null;
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
  activePeerId: string | null;
  startCall: (targetUserId: string, callerInfo: CallerInfo) => void;
  acceptCall: () => void;
  rejectCall: () => void;
  hangUp: () => void;
}

const VideoCallContext = createContext<VideoCallContextValue | null>(null);

export const useVideoCall = () => {
  const ctx = useContext(VideoCallContext);
  if (!ctx) throw new Error('useVideoCall must be used within VideoCallProvider');
  return ctx;
};

export const VideoCallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loginUser } = useAppSelector((state) => state.user);

  const [callState, setCallState] = useState<CallState>('idle');
  const [endReason, setEndReason] = useState<EndReason>(null);
  const [incomingCaller, setIncomingCaller] = useState<CallerInfo | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [activePeerId, setActivePeerId] = useState<string | null>(null);

  const initialized = useRef(false);

  // Poll for the socket becoming available (the messaging provider connects asynchronously)
  useEffect(() => {
    if (!loginUser?.id || initialized.current) return;

    const tryInit = () => {
      const instance = getActiveMessagingInstance();
      const socket = instance?.getSocket();
      if (!socket) return false;

      initialized.current = true;
      videoCallService.init(socket, String(loginUser.id), {
        onStateChange: (state) => {
          setCallState(state);
          if (state === 'idle') {
            setRemoteStream(null);
            setLocalStream(null);
            setIncomingCaller(null);
            setActivePeerId(null);
          } else if (state === 'ended') {
            setRemoteStream(null);
            setLocalStream(null);
          } else {
            setLocalStream(videoCallService.getLocalStream());
          }
        },
        onRemoteStream: (stream) => {
          setRemoteStream(stream);
          setLocalStream(videoCallService.getLocalStream());
        },
        onIncomingCall: (callerInfo) => {
          setIncomingCaller(callerInfo);
          setActivePeerId(callerInfo.userId);
        },
        onError: (err) => {
          console.error('[VideoCall] error:', err);
        },
        onEndReason: (reason) => {
          setEndReason(reason);
        }
      });
      return true;
    };

    if (!tryInit()) {
      // Socket not ready yet — retry until it is
      const interval = setInterval(() => {
        if (tryInit()) clearInterval(interval);
      }, 500);
      return () => clearInterval(interval);
    }

    return () => {
      videoCallService.destroy();
      initialized.current = false;
    };
  }, [loginUser]);

  const startCall = useCallback((targetUserId: string, callerInfo: CallerInfo) => {
    setActivePeerId(targetUserId);
    videoCallService.initiateCall(targetUserId, callerInfo);
  }, []);

  const acceptCall = useCallback(() => {
    videoCallService.acceptCall();
  }, []);

  const rejectCall = useCallback(() => {
    videoCallService.rejectCall();
    setIncomingCaller(null);
    setActivePeerId(null);
  }, []);

  const hangUp = useCallback(() => {
    videoCallService.hangUp();
  }, []);

  return (
    <VideoCallContext.Provider value={{
      callState, endReason, incomingCaller, remoteStream, localStream,
      activePeerId, startCall, acceptCall, rejectCall, hangUp
    }}>
      {children}
    </VideoCallContext.Provider>
  );
};
